import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { Profile } from '../types/db';

interface AuthContextType {
  profile: Profile | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const isLoggingOut = useRef(false);

  useEffect(() => {
    let active = true;

    const loadProfile = async (userId: string) => {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();

      if (!active || isLoggingOut.current) return;
      setProfile(data ?? null);
      setLoading(false);
    };

    // INITIAL SESSION
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data.session?.user) {
        loadProfile(data.session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    // AUTH EVENTS
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active || isLoggingOut.current) return;

      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    isLoggingOut.current = true;
    await supabase.auth.signOut();
    setProfile(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ profile, loading, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
